import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-video-url-player',
  templateUrl: './video-url-player.component.html',
  standalone: false
})
export class VideoUrlPlayerComponent implements OnInit, OnDestroy {
  @Input() dataUrl: string;
  @Output() loadedData = new EventEmitter<boolean>();
  @ViewChild('iframeRef', { static: true }) iframeRef: ElementRef<HTMLIFrameElement>;

  videoUrl: SafeResourceUrl;

  youtubeReady = false;

  isVideoPlaying = false;
  currentTime = 0;
  duration = 0;
  progress = 0;

  // Put your video IDs here
  youtubeId = null;
  vimeoId = null;

  // Use a bound handler so you can remove it later
  private boundOnMessage = this.onMessage.bind(this);


  constructor(private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.getVideoId(this.dataUrl);
    if (this.youtubeId) {
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${this.youtubeId}?enablejsapi=1&origin=${window.location.origin}&controls=0`
      );
    } else {
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://player.vimeo.com/video/${this.vimeoId}?controls=0`
      );
      // Register Vimeo events after iframe is ready
      setTimeout(() => this.registerVimeoEvents(), 1000);
    }
    window.addEventListener('message', this.boundOnMessage);
  }

  registerVimeoEvents() {
    const events = ['play', 'pause', 'ended', 'timeupdate'];
    const win = this.iframeRef.nativeElement.contentWindow;

    events.forEach(event => {
      win?.postMessage({ method: 'addEventListener', value: event }, '*');
    });
    // Request initial info
    this.iframeRef.nativeElement.contentWindow?.postMessage({ method: 'getCurrentTime' }, '*');
    this.iframeRef.nativeElement.contentWindow?.postMessage({ method: 'getDuration' }, '*');
  }

  onMessage(event: MessageEvent) {
    // YouTube messages
    const data = event.data;
    // YouTube
    if (typeof data === 'string' && data.indexOf('infoDelivery') !== -1) {
      try {
        const message = JSON.parse(data);
        const playerState = message.info?.playerState;
        if (message.info?.currentTime) {
          this.currentTime = Math.ceil(message.info?.currentTime);
        }
        if (message.info?.duration && this.duration == 0) {
          this.duration = message.info?.duration;
        }
        this.progress = Math.round((this.currentTime / this.duration) * 100);
        if (playerState === 1) this.isVideoPlaying = true;     // Playing
        if (playerState === 2 || playerState === 0) this.isVideoPlaying = false; // Paused or Ended
      } catch { }
    }

    // Vimeo messages
    // console.log(event.data)
    if (typeof data === 'object' && event.origin.includes('vimeo.com')) {
      if (data.event === 'play') {
        this.isVideoPlaying = true;
      } else if (data.event === 'pause' || data.event === 'ended') {
        this.isVideoPlaying = false;
      }
      if (data?.method === 'setCurrentTime' && data?.value) {
        this.currentTime = data?.value;
      }

      if (data.event === 'timeupdate') {
        if (data.data?.seconds) {
          this.currentTime = data.data?.seconds;
        }
        if (data.data?.duration && this.duration == 0) {
          this.duration = parseInt((data.data?.duration / 10).toFixed(2));
        }
      }
      this.progress = Math.round((this.currentTime / this.duration) * 100);
    }
  }

  getVideoId(url) {
    this.youtubeId = null;
    this.vimeoId = null;
    const youtubeRegex =
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|.+\?v=))([a-zA-Z0-9_-]{11})/;
    const vimeoRegex = /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:\?.*)?$/;
    if (vimeoRegex.test(url)) {
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        this.vimeoId = match[1];

        this.loadedData.emit(true);
      }
    }
    if (youtubeRegex.test(url)) {
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        this.youtubeId = match[1];

        // Set YouTube thumbnail immediately
        this.loadedData.emit(true);
      }
    }
  }

  toggleVideo() {
    if (this.youtubeId) {
      if (!this.isVideoPlaying) {
        this.youtubePlay();
      } else {
        this.youtubePause()
      }
    } else {
      if (!this.isVideoPlaying) {
        this.vimeoPlay();
      } else {
        this.vimeoPause()
      }
    }
  }
  // YouTube controls
  youtubePlay() {
    if (!this.youtubeReady) {
      this.checkYouTubeStatus();
      this.isVideoPlaying = true;
    }
    this.iframeRef.nativeElement.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
      '*'
    );
  }

  youtubePause() {
    this.isVideoPlaying = false;

    this.iframeRef.nativeElement.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
      '*'
    );
  }

  checkYouTubeStatus() {
    this.iframeRef.nativeElement.contentWindow?.postMessage(
      JSON.stringify({ event: 'listening' }),
      '*'
    );
  }

  seekVideoTo(e: PointerEvent | any, offsetWidth) {
    const precentage = Math.round((e.offsetX / offsetWidth) * 100) / 100;
    const seconds = this.duration * precentage;
    if (this.youtubeId) {
      this.seekYouTubeTo(seconds);
    } else {
      this.seekVimeoTo(seconds);
    }
  }

  seekYouTubeTo(seconds: number) {
    this.iframeRef.nativeElement.contentWindow?.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'seekTo',
        args: [seconds, true] // true = allowSeekAhead
      }),
      '*'
    );
  }

  // Vimeo controls
  vimeoPlay() {
    this.seekVimeoTo(0); // Reset time
    this.isVideoPlaying = true;

    this.iframeRef.nativeElement.contentWindow?.postMessage(
      { method: 'play' },
      '*'
    );
  }

  vimeoPause() {
    this.isVideoPlaying = false;
    this.iframeRef.nativeElement.contentWindow?.postMessage(
      { method: 'pause' },
      '*'
    );
  }

  seekVimeoTo(seconds: number) {
    this.iframeRef.nativeElement.contentWindow?.postMessage(
      {
        method: 'setCurrentTime',
        value: seconds
      },
      '*'
    );
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.boundOnMessage);
  }
}
